import {
  Action, FormHandlers, Portal, useModal,
} from '@doveit/bricks';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import { ReferenceType } from '../../../domain/types';
import { Rumor } from '../../../providers/api/dtos/rumor';
import UpsertReminder, { UpsertReminderProps } from '../../../reminders/containers/upsert-reminder/UpsertReminder';
import useFilters, { Filters } from '../../../hooks/use-filters/useFilters';
import CreateRumor from '../../containers/create-rumor/CreateRumor';
import SimplePageLayout from '../../../layouts/simple-page-layout/SimplePageLayout';

interface CreateRumorPageFilters extends Filters {
  contactId?: string;
}

const CreateRumorPage: React.FC = () => {
  const navigate = useNavigate();
  const { filters: { contactId } } = useFilters<CreateRumorPageFilters>();
  const formRef = React.useRef<FormHandlers>() as React.MutableRefObject<FormHandlers>;
  const addReminderModal = useModal<UpsertReminderProps>();
  const [rumor, setRumor] = React.useState<Rumor | undefined>();
  const [isSavingReminder, setIsSavingReminder] = React.useState(false);

  const onSuccess = React.useCallback((createdRumor: Rumor) => {
    setRumor(createdRumor);
    addReminderModal.open({
      reminder: {
        agentId: createdRumor.agentId,
        referenceId: createdRumor.id!,
        referenceType: ReferenceType.RUMOR,
        resolved: false,
      },
    });
  }, [addReminderModal]);

  const goToRumorPage = React.useCallback(() => {
    if (rumor) {
      navigate(`/rumors/${rumor.id}`);
    }
  }, [navigate, rumor]);

  const saveReminder = React.useCallback(async () => {
    const form = formRef.current as any;
    setIsSavingReminder(true);

    const errors = await form.validateForm();
    form.handleSubmit();

    if (Object.keys(errors).length > 0) {
      setIsSavingReminder(false);
    }
  }, []);

  const onAddReminderSuccess = React.useCallback(() => {
    setIsSavingReminder(false);
    goToRumorPage();
  }, [goToRumorPage]);

  const onAddReminderError = React.useCallback(() => {
    setIsSavingReminder(false);
  }, []);

  return (
    <SimplePageLayout
      boxed
      wrapperSize="M"
    >
      <CreateRumor
        contactId={contactId}
        onSuccess={onSuccess}
      />
      {addReminderModal.data && (
        <Portal>
          <SimpleModal
            {...addReminderModal}
            title="Vuoi aggiungere un promemoria?"
            footer={(
              <>
                <Action
                  label="Salta"
                  onClick={goToRumorPage}
                  disabled={isSavingReminder}
                />
                <Action
                  label="Crea promemoria"
                  color="primary"
                  emphasis="high"
                  onClick={saveReminder}
                  loading={isSavingReminder}
                />
              </>
            )}
          >
            <UpsertReminder
              {...addReminderModal.data}
              onSuccess={onAddReminderSuccess}
              onError={onAddReminderError}
              formRef={formRef}
              hideSubmit
            />
          </SimpleModal>
        </Portal>
      )}
    </SimplePageLayout>
  );
};

export default CreateRumorPage;
